import { ART_PALETTE } from '../art-palette.js';
import { MASK, PIPE_MASKS } from './masks.js';
import { blankPixels, floraPixels, footprintTile, plainTile } from './helpers.js';

// Shared material ramps (art-direction-rules §0 house style, §7 ramp reuse).
// Wood everywhere in this file is 3 / 7 / 8 with Sand (9) as the lit cap.
// Every stone or metal surface walks the cool Stone ramp 24-27, so a barrel
// hoop and an anvil body speak the same material language.
const INK = 1;
const WOOD_DK = 3;   // Brown shadow — wood underside / rim
const WOOD = 7;      // Earth dark — wood body
const WOOD_MD = 8;   // Earth — lit wood plane
const WOOD_HI = 9;   // Sand — wood lit cap
const CREAM = 11;    // Warm accent / paper / lamp core
const SHADOW = 13;   // Dark grass — flat contact shadow, drawn behind the prop
const WATER_DK = 19;
const WATER = 20;
const WATER_HI = 21;
const STONE_SH = 24; // Stone shadow — underside
const STONE_DK = 25; // Stone dark — body
const STONE = 26;    // Stone — lit plane
const STONE_HI = 27; // Stone light — small specular cap
const RED = 28;      // Signal red accent
const CLOTH = 29;    // Slate blue — soft goods
const BERRY = 30;    // Berry accent

const DIRS = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];

/** Wrap every opaque cluster in a 1px Ink contour, diagonal corner gaps included. */
function inkContour(px) {
  const height = px.length;
  const width = px[0].length;
  const copy = px.map((row) => [...row]);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (copy[y][x]) continue;
      if (DIRS.some(([dx, dy]) => copy[y + dy]?.[x + dx])) px[y][x] = INK;
    }
  }
}

/**
 * Flat Dark-grass contact ellipse. Painted last but only into still-empty
 * pixels, so it reads as ground shadow *behind* the already-contoured prop.
 */
function groundShadow(px, cx, cy, rx, ry) {
  const height = px.length;
  const width = px[0].length;
  for (let y = Math.max(0, Math.ceil(cy - ry)); y <= Math.min(height - 1, cy + ry); y++) {
    for (let x = Math.max(0, Math.ceil(cx - rx)); x <= Math.min(width - 1, cx + rx); x++) {
      if (px[y][x]) continue;
      if (((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1) px[y][x] = SHADOW;
    }
  }
}

function largeObjectCells(width, height, draw) {
  const canvas = Array.from({ length: height * 16 }, () => Array(width * 16).fill(0));
  const put = (x, y, c) => {
    if (x >= 0 && x < width * 16 && y >= 0 && y < height * 16) canvas[y][x] = c;
  };
  const rect = (x, y, w, h, c) => {
    for (let py = y; py < y + h; py++) for (let px = x; px < x + w; px++) put(px, py, c);
  };
  draw({ put, rect, canvas });
  const cells = [];
  for (let cy = height - 1; cy >= 0; cy--) {
    for (let cx = 0; cx < width; cx++) {
      cells.push(Array.from({ length: 16 }, (_, y) => canvas[cy * 16 + y].slice(cx * 16, cx * 16 + 16)));
    }
  }
  return cells;
}

function landmarkCells(kind) {
  return largeObjectCells(2, 2, ({ put, rect, canvas }) => {
    if (kind === 'market-stall') {
      // Ridge beam
      rect(2, 3, 28, 2, WOOD);
      rect(2, 3, 28, 1, WOOD_HI);
      // Awning: two flat cloth colours, no shading noise. Ink contour closes it.
      for (let x = 2; x < 30; x++) {
        rect(x, 5, 1, 5, Math.floor((x - 2) / 4) % 2 ? RED : CREAM);
      }
      // Posts
      for (const px of [4, 25]) {
        rect(px, 10, 3, 19, WOOD);
        rect(px, 10, 1, 19, WOOD_MD);
        rect(px + 2, 10, 1, 19, WOOD_DK);
      }
      // Produce crate + goods on the counter top
      rect(21, 14, 6, 5, WOOD);
      rect(21, 14, 6, 1, WOOD_HI);
      rect(26, 15, 1, 4, WOOD_DK);
      rect(7, 16, 3, 3, RED);
      rect(12, 16, 3, 3, 17);
      rect(16, 16, 3, 3, RED);
      // Counter: lit top plane, body, dark apron
      rect(3, 19, 26, 1, WOOD_HI);
      rect(3, 20, 26, 2, WOOD_MD);
      rect(3, 22, 26, 1, WOOD_DK);
      rect(5, 23, 22, 3, WOOD);
      rect(5, 25, 22, 1, WOOD_DK);
      inkContour(canvas);
      groundShadow(canvas, 15.5, 30.5, 14, 2.5);
    } else if (kind === 'well') {
      // Shingle roof — one flat plane, lit left half, dark eave band.
      const roof = [[11, 20, 2], [9, 22, 3], [7, 24, 4], [5, 26, 5], [4, 27, 6], [3, 28, 7], [3, 28, 8]];
      for (const [x0, x1, y] of roof) {
        for (let x = x0; x <= x1; x++) {
          put(x, y, y <= 3 ? WOOD_HI : y >= 7 ? WOOD_DK : (x < 16 ? WOOD_MD : WOOD));
        }
      }
      // Posts
      for (const px of [6, 23]) {
        rect(px, 9, 3, 10, WOOD);
        rect(px, 9, 1, 10, WOOD_MD);
        rect(px + 2, 9, 1, 10, WOOD_DK);
      }
      // Rope + bucket
      rect(15, 9, 1, 4, WOOD_DK);
      rect(13, 13, 5, 5, WOOD);
      rect(13, 13, 5, 1, WOOD_HI);
      rect(17, 14, 1, 4, WOOD_DK);
      // Stone basin: lit rim cap, rim plane, dark shaft, front wall.
      rect(5, 18, 22, 1, STONE_HI);
      rect(5, 19, 22, 2, STONE);
      rect(8, 19, 16, 2, STONE_SH);
      rect(9, 19, 14, 1, WATER_DK);
      rect(5, 21, 22, 7, STONE_DK);
      rect(5, 21, 3, 7, STONE);
      rect(5, 27, 22, 1, STONE_SH);
      rect(12, 22, 1, 5, STONE_SH);
      rect(19, 22, 1, 5, STONE_SH);
      inkContour(canvas);
      groundShadow(canvas, 15.5, 30, 13, 2.5);
    } else {
      // fountain — cool stone tiers; the jet is one connected spray, not specks.
      rect(14, 2, 4, 1, WATER_HI);
      rect(12, 3, 8, 1, WATER_HI);
      rect(15, 4, 2, 5, WATER_HI);
      rect(11, 4, 2, 1, WATER);
      rect(18, 4, 2, 1, WATER);
      rect(10, 5, 2, 1, WATER);
      rect(20, 5, 2, 1, WATER);
      rect(10, 6, 1, 3, WATER);
      rect(21, 6, 1, 3, WATER);
      // Upper bowl
      rect(8, 9, 16, 1, STONE_HI);
      rect(8, 10, 16, 2, STONE);
      rect(10, 10, 12, 1, WATER);
      rect(8, 12, 16, 1, STONE_DK);
      rect(10, 13, 12, 1, STONE_SH);
      // Pedestal
      rect(14, 14, 4, 6, STONE_DK);
      rect(14, 14, 1, 6, STONE);
      rect(17, 14, 1, 6, STONE_SH);
      // Lower basin
      rect(3, 20, 26, 1, STONE_HI);
      rect(3, 21, 26, 2, STONE);
      rect(6, 21, 20, 2, WATER_DK);
      rect(6, 21, 10, 1, WATER);
      rect(3, 23, 26, 6, STONE_DK);
      rect(3, 23, 3, 6, STONE);
      rect(3, 28, 26, 1, STONE_SH);
      rect(10, 24, 1, 4, STONE_SH);
      rect(21, 24, 1, 4, STONE_SH);
      inkContour(canvas);
      groundShadow(canvas, 15.5, 31, 15, 2.5);
    }
  });
}

/** Lantern head shared by every light post (local warm emissive accent). */
function lanternHead(rect) {
  rect(8, 1, 1, 1, STONE);
  rect(5, 2, 7, 1, STONE);
  rect(4, 3, 9, 1, STONE_DK);
  rect(4, 4, 9, 6, STONE_SH);
  rect(6, 5, 5, 4, WOOD_HI);
  rect(7, 6, 3, 2, CREAM);
  rect(4, 10, 9, 1, STONE_DK);
}

function tallLampCells(kind) {
  return largeObjectCells(1, 2, ({ rect, canvas }) => {
    lanternHead(rect);
    // Cool metal post: flat body, one lit left edge, dark right edge.
    rect(7, 11, 3, 16, STONE_DK);
    rect(7, 11, 1, 16, STONE);
    rect(9, 11, 1, 16, STONE_SH);
    rect(5, 27, 7, 2, STONE_DK);
    rect(5, 27, 7, 1, STONE);
    if (kind === 'twin') {
      rect(2, 12, 12, 2, STONE_DK);
      rect(2, 12, 12, 1, STONE);
      for (const x of [1, 11]) {
        rect(x, 14, 4, 5, STONE_SH);
        rect(x + 1, 15, 2, 3, WOOD_HI);
        rect(x + 1, 16, 2, 1, CREAM);
      }
    }
    if (kind === 'festival') {
      rect(0, 12, 16, 2, WOOD);
      rect(0, 12, 16, 1, WOOD_HI);
      const flags = [[0, RED], [4, CREAM], [9, BERRY], [13, WOOD_HI]];
      for (const [x, colour] of flags) {
        rect(x, 14, 3, 2, colour);
        rect(x + 1, 16, 1, 1, colour);
      }
    }
    inkContour(canvas);
    groundShadow(canvas, 8, 31, 7, 2.5);
  });
}

/**
 * Wooden fence (pipe4). Post lives inside rows 4-11 so the outer three-pixel
 * seam band carries rail cross-sections only; every legal join therefore meets
 * an identical band. Free ends still get the full Ink contour.
 */
function fenceVariants() {
  return Object.fromEntries(PIPE_MASKS.map((mask) => {
    const p = blankPixels();
    const rect = (x, y, w, h, c) => {
      for (let py = y; py < y + h; py++) {
        for (let px = x; px < x + w; px++) {
          if (px >= 0 && px < 16 && py >= 0 && py < 16) p[py][px] = c;
        }
      }
    };
    // Rail cross-section: lit cap, body, dark underside.
    const hrail = (x, w) => {
      for (const y of [5, 9]) {
        rect(x, y, w, 1, WOOD_HI);
        rect(x, y + 1, w, 1, WOOD_MD);
        rect(x, y + 2, w, 1, WOOD_DK);
      }
    };
    // A north/south run is seen edge-on, so its rails foreshorten into one
    // narrow strip that threads straight through the post.
    const vrail = (y, h) => {
      rect(6, y, 1, h, WOOD_MD);
      rect(7, y, 2, h, WOOD);
      rect(9, y, 1, h, WOOD_DK);
    };
    if (mask & MASK.W) hrail(0, 5);
    if (mask & MASK.E) hrail(11, 5);
    if (mask & MASK.N) vrail(0, 5);
    if (mask & MASK.S) vrail(11, 5);
    // Post, drawn over the rails.
    rect(5, 4, 6, 1, WOOD_HI);
    rect(5, 5, 6, 6, WOOD);
    rect(5, 5, 1, 6, WOOD_MD);
    rect(10, 5, 1, 6, WOOD_DK);
    rect(5, 11, 6, 1, WOOD_DK);
    inkContour(p);
    // Contact shadow hugs the post base; a vertical run occludes it entirely.
    const pads = mask & MASK.S ? [[12, 4, 11]] : [[12, 4, 11], [13, 3, 12]];
    for (const [y, x0, x1] of pads) {
      for (let x = x0; x <= x1; x++) if (!p[y][x]) p[y][x] = SHADOW;
    }
    return [mask, p];
  }));
}

function townObjectPixels(kind, frame = 0) {
  let shade = [7.5, 15, 6, 2];
  return floraPixels(({ pixels, put, rect }) => {
    if (kind.startsWith('chair')) {
      const blue = kind.includes('blue');
      const side = kind.includes('side');
      const seatHi = blue ? STONE : WOOD_HI;
      const seat = blue ? CLOTH : WOOD_MD;
      const seatDk = blue ? STONE_SH : WOOD_DK;
      if (side) {
        rect(3, 2, 2, 7, WOOD);
        rect(3, 2, 1, 7, WOOD_MD);
        rect(3, 2, 2, 1, WOOD_HI);
        rect(3, 9, 9, 1, seatHi);
        rect(3, 10, 9, 1, seat);
        rect(3, 11, 9, 1, seatDk);
        for (const x of [3, 10]) {
          rect(x, 12, 2, 2, WOOD);
          rect(x + 1, 12, 1, 2, WOOD_DK);
        }
      } else {
        rect(4, 2, 8, 2, WOOD);
        rect(4, 2, 8, 1, WOOD_HI);
        rect(11, 3, 1, 1, WOOD_DK);
        rect(4, 4, 2, 4, WOOD);
        rect(4, 4, 1, 4, WOOD_MD);
        rect(10, 4, 2, 4, WOOD);
        rect(11, 4, 1, 4, WOOD_DK);
        rect(3, 8, 10, 1, seatHi);
        rect(3, 9, 10, 1, seat);
        rect(3, 10, 10, 1, seatDk);
        for (const x of [4, 10]) {
          rect(x, 11, 2, 3, WOOD);
          rect(x + 1, 11, 1, 3, WOOD_DK);
        }
      }
    } else if (kind.startsWith('bench')) {
      const stone = kind.includes('stone');
      const cap = stone ? STONE : WOOD_HI;
      const body = stone ? STONE_DK : WOOD;
      const dark = stone ? STONE_SH : WOOD_DK;
      // Back rail
      rect(1, 3, 14, 1, cap);
      rect(1, 4, 14, 1, body);
      rect(1, 5, 14, 1, dark);
      // Struts
      for (const x of [2, 12]) rect(x, 6, 2, 3, body);
      // Seat
      rect(1, 9, 14, 1, cap);
      rect(1, 10, 14, 1, body);
      rect(1, 11, 14, 1, dark);
      // Legs
      for (const x of [2, 11]) {
        rect(x, 12, 3, 2, body);
        rect(x + 2, 12, 1, 2, dark);
      }
      if (stone) {
        put(1, 3, STONE_HI);
        put(2, 3, STONE_HI);
        put(1, 9, STONE_HI);
        put(2, 9, STONE_HI);
      }
      if (kind.includes('flower')) {
        rect(2, 1, 12, 2, SHADOW);
        for (const [fx, colour] of [[3, RED], [7, CREAM], [11, BERRY]]) {
          rect(fx - 1, 1, 3, 1, colour);
          put(fx, 2, colour);
        }
      }
      shade = [7.5, 15, 7, 2];
    } else if (kind === 'barrel') {
      rect(4, 2, 8, 12, WOOD);
      rect(4, 2, 3, 12, WOOD_MD);
      rect(11, 2, 1, 12, WOOD_DK);
      rect(4, 2, 8, 1, WOOD_HI);
      rect(4, 5, 8, 1, STONE_DK);
      rect(4, 10, 8, 1, STONE_DK);
    } else if (kind === 'crate') {
      rect(3, 3, 10, 11, WOOD);
      rect(5, 5, 6, 7, WOOD_MD);
      rect(5, 5, 6, 1, WOOD_HI);
      rect(10, 5, 1, 7, WOOD_DK);
      rect(3, 8, 10, 1, WOOD_DK);
      rect(3, 13, 10, 1, WOOD_DK);
    } else if (kind === 'signpost') {
      rect(7, 8, 2, 6, WOOD);
      rect(8, 8, 1, 6, WOOD_DK);
      rect(2, 2, 12, 7, WOOD_MD);
      rect(2, 2, 12, 1, WOOD_HI);
      rect(2, 8, 12, 1, WOOD_DK);
      rect(4, 4, 7, 1, WOOD_DK);
      rect(4, 6, 5, 1, WOOD_DK);
      shade = [7.5, 15, 5, 2];
    } else if (kind === 'notice-board') {
      rect(2, 1, 12, 1, WOOD_HI);
      rect(2, 2, 12, 9, WOOD);
      rect(13, 2, 1, 9, WOOD_DK);
      rect(3, 3, 10, 6, WOOD_DK);
      rect(4, 4, 4, 4, CREAM);
      rect(9, 5, 3, 3, WOOD_HI);
      rect(5, 5, 2, 1, WOOD_DK);
      rect(5, 6, 3, 1, WOOD_DK);
      for (const x of [4, 10]) {
        rect(x, 11, 2, 3, WOOD);
        rect(x + 1, 11, 1, 3, WOOD_DK);
      }
    } else if (kind === 'planter') {
      rect(3, 6, 10, 3, SHADOW);
      for (const [fx, fy, colour] of [[4, 4, RED], [8, 3, CREAM], [11, 5, BERRY]]) {
        rect(fx - 1, fy, 3, 2, colour);
        rect(fx, fy + 2, 1, 7 - fy, SHADOW);
      }
      rect(2, 9, 12, 1, WOOD_HI);
      rect(2, 10, 12, 3, WOOD);
      rect(13, 10, 1, 3, WOOD_DK);
      rect(2, 13, 12, 1, WOOD_DK);
      shade = [7.5, 15, 7, 2];
    } else if (kind === 'hay-bale') {
      // Squat and wide, so it never reads as a second barrel.
      rect(3, 6, 10, 1, WOOD_HI);
      rect(1, 7, 14, 1, WOOD_HI);
      rect(1, 8, 14, 4, WOOD_MD);
      rect(1, 8, 1, 2, WOOD_HI);
      rect(1, 12, 14, 1, WOOD);
      rect(2, 13, 12, 1, WOOD);
      rect(5, 6, 1, 8, WOOD_DK);
      rect(10, 6, 1, 8, WOOD_DK);
      shade = [7.5, 15, 8, 2];
    } else if (kind === 'anvil') {
      put(1, 5, STONE);
      put(1, 6, STONE_DK);
      rect(2, 4, 12, 1, STONE);
      rect(2, 4, 2, 1, STONE_HI);
      rect(2, 5, 12, 2, STONE_DK);
      rect(13, 5, 1, 2, STONE_SH);
      rect(6, 7, 4, 4, STONE_DK);
      rect(9, 7, 1, 4, STONE_SH);
      rect(4, 11, 8, 1, STONE);
      rect(4, 12, 8, 1, STONE_DK);
      rect(4, 13, 8, 1, STONE_SH);
    } else if (kind === 'campfire') {
      // Emissive: a local warm accent that deliberately ignores the key light.
      // Four-frame flicker loop (art rules §9): the upper tongues sway one
      // pixel and the tip dips into a hotter core on frame 2, while the base
      // rows and log bed stay identical on every frame so the ground contact
      // and the light origin never pop.
      const sway = [0, -1, 0, 1][frame];
      if (frame !== 2) rect(7 + sway, 3, 2, 1, RED);
      rect(6 + sway, 4, 4, 2, RED);
      rect(7 + sway, 4, 2, frame === 2 ? 1 : 2, WOOD_HI);
      if (frame === 2) rect(7, 5, 2, 1, CREAM);
      rect(5 + sway, 6, 6, 2, RED);
      rect(6 + sway, 6, 4, 2, WOOD_HI);
      rect(7 + sway, 6, 2, 2, CREAM);
      rect(4, 8, 8, 4, RED);
      rect(5, 8, 6, 2, WOOD_HI);
      rect(6, 8, 4, 2, CREAM);
      // Log bed, drawn over the flame base.
      rect(2, 11, 12, 1, WOOD_HI);
      rect(2, 12, 12, 1, WOOD);
      rect(2, 13, 12, 1, WOOD_DK);
      rect(2, 12, 1, 1, WOOD_DK);
      rect(13, 12, 1, 1, WOOD_DK);
      rect(4, 12, 2, 1, WOOD_DK);
      rect(9, 12, 2, 1, WOOD_DK);
      shade = [7.5, 15, 7, 2];
    }
    inkContour(pixels);
    groundShadow(pixels, ...shade);
  });
}

/**
 * Modular table (pipe4).
 * The surface is one flat plane. Free sides take a lit cap (N/W) or a dark rim
 * plus Ink contour and a Dark-grass drop shadow (S/E); connected sides run the
 * plane straight off the tile edge so a legal join has no contour at all.
 */
function modularTableVariants() {
  return Object.fromEntries(PIPE_MASKS.map((mask) => {
    const p = blankPixels();
    const n = Boolean(mask & MASK.N);
    const e = Boolean(mask & MASK.E);
    const s = Boolean(mask & MASK.S);
    const w = Boolean(mask & MASK.W);

    const x0 = w ? 0 : 1;
    const x1 = e ? 15 : 13;
    const y0 = n ? 0 : 1;
    const y1 = s ? 15 : 13;

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) p[y][x] = WOOD_MD;
    }
    // Two plank separators — the only marks on the plane. They span the full
    // width so an east/west join continues them without a break.
    for (const y of [5, 10]) {
      if (y < y0 || y > y1) continue;
      for (let x = x0; x <= x1; x++) p[y][x] = WOOD;
    }
    if (!n) for (let x = x0; x <= x1; x++) p[y0][x] = WOOD_HI;
    if (!w) for (let y = y0; y <= y1; y++) p[y][x0] = WOOD_HI;
    if (!s) for (let x = x0; x <= x1; x++) p[y1][x] = WOOD_DK;
    if (!e) for (let y = y0; y <= y1; y++) p[y][x1] = WOOD_DK;
    if (!n && !w) p[y0][x0] = WOOD_HI;

    inkContour(p);

    if (!s) for (let x = Math.max(0, x0 - 1); x <= Math.min(15, x1 + 1); x++) {
      if (!p[15][x]) p[15][x] = SHADOW;
    }
    if (!e) for (let y = Math.max(0, y0 - 1); y <= Math.min(15, y1 + 1); y++) {
      if (!p[y][15]) p[y][15] = SHADOW;
    }
    if (!s && !e && !p[15][15]) p[15][15] = SHADOW;

    return [mask, p];
  }));
}

const TOWN_OBJECTS = [
  ['chair-oak', 'Oak chair', 'chair'],
  ['chair-blue', 'Blue chair', 'chair-blue'],
  ['chair-side', 'Side-facing chair', 'chair-side'],
  ['bench-wood', 'Wooden bench', 'bench'],
  ['bench-stone', 'Stone bench', 'bench-stone'],
  ['flower-bench', 'Flowering bench', 'bench-flower'],
  ['barrel', 'Barrel', 'barrel'],
  ['crate', 'Shipping crate', 'crate'],
  ['signpost', 'Town signpost', 'signpost'],
  ['notice-board', 'Notice board', 'notice-board'],
  ['flower-planter', 'Flower planter', 'planter'],
  ['hay-bale', 'Hay bale', 'hay-bale'],
  ['anvil', 'Blacksmith anvil', 'anvil'],
  ['campfire', 'Campfire', 'campfire'],
].map(([key, name, kind]) =>
  plainTile({
    key: `object.${key}`,
    name,
    channel: 'onGround',
    section: 'townObjects',
    component: 'onGround',
    pixels: townObjectPixels(kind),
    collision: { ground: true },
    ...(kind === 'campfire' ? {
      light: { radiusTiles: 2.5, intensity: 1, origin: { x: 0.5, y: 0.45 } },
      animation: {
        component: 'onGround',
        frameDurationMs: 160,
        variantFrames: Array.from({ length: 4 }, (_, frame) => ({ 0: townObjectPixels(kind, frame) })),
      },
    } : {}),
  }),
);

const LARGE_TOWN_OBJECTS = [
  ['well', 'Village well', 'well'],
  ['fountain', 'Little fountain', 'fountain'],
  ['market-stall', 'Market stall', 'market-stall'],
].map(([key, name, kind]) =>
  footprintTile({
    key: `object.${key}`,
    name,
    channel: 'onGround',
    section: 'townObjects',
    component: 'onGround',
    width: 2,
    height: 2,
    cells: landmarkCells(kind),
    collision: { ground: true },
  }),
);

const LIGHT_POSTS = [
  ['lamp-post', 'Town lamp post', 'single', 4],
  ['twin-lamp-post', 'Twin lamp post', 'twin', 4.5],
  ['festival-light-post', 'Festival light post', 'festival', 4],
].map(([key, name, kind, radiusTiles]) =>
  footprintTile({
    key: `object.${key}`,
    name,
    channel: 'onGround',
    section: 'townLights',
    component: 'onGround',
    width: 1,
    height: 2,
    cells: tallLampCells(kind),
    collision: { ground: true },
    light: { radiusTiles, intensity: 1, origin: { x: 0.5, y: -0.65 } },
  }),
);

const MODULAR_TABLE = {
  key: 'object.modular-table',
  name: 'Modular table',
  channel: 'onGround',
  mode: 'pipe4',
  connectionGroup: 'modular-table',
  paletteSection: 'townObjects',
  palette: ART_PALETTE,
  collision: { ground: true },
  components: { onGround: modularTableVariants() },
};

const WOODEN_FENCE = {
  key: 'object.wooden-fence',
  name: 'Wooden fence',
  channel: 'onGround',
  mode: 'pipe4',
  connectionGroup: 'wooden-fence',
  paletteSection: 'fences',
  palette: ART_PALETTE,
  collision: { ground: true },
  components: { onGround: fenceVariants() },
};

export { TOWN_OBJECTS, LARGE_TOWN_OBJECTS, LIGHT_POSTS, MODULAR_TABLE, WOODEN_FENCE };
